/* eslint react/destructuring-assignment: "off" */
/* eslint react-hooks/exhaustive-deps: "off" */
/* eslint react/require-default-props: "off" */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormControl, Row, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import * as API from 'api/api';
import { validateCustomFields } from 'common/customFieldValidation';
import * as errors from 'common/errors';
import * as tracker from 'common/tracker';
import * as validator from 'email-validator';
import useInterval from 'hooks/useInterval';
import useQuery from 'hooks/useQuery';

import Tick from 'assets/svg/tick.svg?react';
import CampaignList from 'components/subscribe/property/CampaignList';
import PageHeader from 'components/subscribe/property/PageHeader';
import CustomFieldInput from '../customfields/CustomFieldInput';

import { PropertyBranding } from 'common/types';

import '../Base-Subscribe.css';
import './Embed-Subscribe.css';

const prepareName = (name: string) =>
  name.trim() === '' ? undefined : name.trim();

const Name = ({
  disabled,
  label,
  onChange,
  onKeyPress,
  value,
}: {
  disabled: boolean;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  value: string;
}) => (
  <div className="d-block w-100 subscribe__property__content__firstname">
    <div className="subscribe__property__content__label">{label}</div>
    <FormControl
      className="subscribe__property__content__input"
      type="text"
      value={value}
      disabled={disabled}
      onChange={onChange}
      onKeyPress={onKeyPress}
    />
  </div>
);

const SignupButton = ({
  disabled,
  isBusy,
  label,
  onClick,
}: {
  disabled: boolean;
  isBusy: boolean;
  label: string;
  onClick: () => unknown;
}) => (
  <Button
    className="subscribe__content__button ebx-btn-lg"
    type="button"
    disabled={disabled}
    onClick={onClick}
  >
    {!isBusy ? (
      label
    ) : (
      <div>
        <Spinner
          as="span"
          animation="border"
          size="sm"
          role="status"
          aria-hidden="true"
          className="mr-2"
        />
      </div>
    )}
  </Button>
);

const PropertySubscribe = ({ branding }: { branding?: PropertyBranding }) => {
  const { pathname } = useLocation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, any>
  >({});
  const [customFieldErrors, setCustomFieldErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [isSigningUp, setSigningUp] = useState(false);
  const [isSignupComplete, setSignupComplete] = useState(
    pathname.endsWith('/complete')
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCountingDown, setCountingDown] = useState(false);
  const [timeRemainingSecs, setTimeRemainingSecs] = useState(3);

  const pageInstanceId = useRef(0);

  const queryParams = useQuery();
  const isEmbeddedMode = queryParams.get('embed') !== null;
  const isSuccessPageTest = queryParams.get('complete') !== null;
  const hasNoScrolling = queryParams.get('noscroll') !== null;

  useEffect(() => {
    if (branding) {
      /* Generate random nine-digit number starting with non-zero digit */
      pageInstanceId.current = Math.floor(
        100000000 + Math.random() * 900000000
      );
      tracker.track({
        eventName: 'Display Subscription Page',
        trackingParams: {
          'Subscription page displayed to reader': window.location.href,
          '# campaigns offered': branding.orderedCampaigns.length,
          'Page instance ID': pageInstanceId.current,
          'Subscription Page': 'Property',
        },
      });
    }
  }, []);

  useInterval(
    // Callback - code to be executed each interval
    () => {
      if (timeRemainingSecs > 0) {
        setTimeRemainingSecs(timeRemainingSecs - 1);
      } else {
        setCountingDown(false);
        handleRedirect();
      }
    },
    // Interval - return null to stop the interval when the countdown reaches zero
    isCountingDown ? 1000 : null
  );

  const initialSelectedCampaigns = branding?.orderedCampaigns.map(
    campaignURNAndName => ({
      ...campaignURNAndName,
      isSelected: false,
    })
  );

  const [selectedCampaigns, setSelectedCampaigns] = useState(
    initialSelectedCampaigns
  );

  let selectedCampaignCount = 0;
  selectedCampaigns?.forEach(campaignURNAndName => {
    selectedCampaignCount += campaignURNAndName.isSelected ? 1 : 0;
  });

  const validEmail = validator.validate(emailAddress.trim());

  const disableButton = useMemo(() => {
    if (isSigningUp || selectedCampaignCount === 0 || !validEmail) {
      return true;
    }

    // Check mandatory custom fields are filled
    if (branding?.customFieldsConfig && branding?.customFieldDefinitions) {
      const mandatoryFields = branding.customFieldsConfig.enabledFields.filter(
        field => field.isMandatory
      );

      for (const field of mandatoryFields) {
        const definition = branding.customFieldDefinitions.find(
          def => def.dataName === field.dataName
        );
        if (!definition) continue;

        const value = customFieldValues[definition.displayName];
        if (value === undefined || value === null || value === '') {
          return true;
        }
        // For arrays (multi-select), check if empty
        if (Array.isArray(value) && value.length === 0) {
          return true;
        }
      }
    }

    return false;
  }, [
    firstName,
    isSigningUp,
    lastName,
    selectedCampaignCount,
    validEmail,
    customFieldValues,
    branding,
  ]);

  if (!branding) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(event.target.value);
    setErrorMessage(null);
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFirstName(event.target.value);
    setErrorMessage(null);
  };

  const handleKeypress = (event: React.KeyboardEvent) => {
    setErrorMessage(null);
    if (event.charCode === 13 && !disableButton) {
      handleSubmit();
    }
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
    setErrorMessage(null);
  };

  const handleRedirect = () => {
    const hasRedirectURL = branding?.redirectURL && branding.redirectURL !== '';
    if (hasRedirectURL) {
      window.location.href = branding.redirectURL;
    }
  };

  const handleSubmit = async () => {
    // Validate custom fields
    if (branding?.customFieldsConfig && branding?.customFieldDefinitions) {
      const validationErrors = validateCustomFields(
        branding.customFieldsConfig.enabledFields,
        customFieldValues,
        branding.customFieldDefinitions
      );

      if (validationErrors.length > 0) {
        const errorMap: Record<string, string> = {};
        validationErrors.forEach(err => {
          // Find displayName for the error field
          const definition = branding.customFieldDefinitions!.find(
            def => def.dataName === err.dataName
          );
          if (definition) {
            errorMap[definition.displayName] = err.message;
          }
        });
        setCustomFieldErrors(errorMap);
        setErrorMessage('Please fix the errors in the form');
        return;
      }
    }

    setSigningUp(true);
    setCustomFieldErrors({});

    const campaignsToSubscribe = selectedCampaigns?.filter(
      campaign => campaign.isSelected
    );
    // Create comma-separated string of campaign URNs, trimming whitespace
    const campaignURNs =
      campaignsToSubscribe
        ?.map(campaign => campaign.campaignURN.trim())
        .filter(urn => urn.length > 0)
        .join(',') ?? '';

    if (!campaignURNs) {
      setErrorMessage('Please select at least one campaign');
      setSigningUp(false);
      return;
    }

    try {
      await API.postSubscribe({
        campaignKey: campaignURNs,
        emailAddress: emailAddress.trim(),
        firstName: prepareName(firstName),
        lastName: prepareName(lastName),
        customFields: customFieldValues,
      });
      tracker.track({
        eventName: 'Reader Subscribe',
        trackingParams: {
          '# campaigns offered': branding.orderedCampaigns.length,
          '# campaigns subscribed to': campaignURNs.split(',').length,
          'Page instance ID': pageInstanceId.current,
          'Subscription Page': 'Property',
        },
      });
      setSignupComplete(true);
      setSigningUp(false);
      setCountingDown(true);
    } catch (error) {
      console.log(error);
      let errorMsg = errors.getErrorMessage(error);
      if (errorMsg === 'Internal Server Error') {
        errorMsg = 'Unable to subscribe, please try again';
      }
      if (errorMsg.includes('action could not be completed')) {
        errorMsg = 'Please check you have entered a valid email address';
      }
      setErrorMessage(errorMsg);
      setSigningUp(false);
    }
  };

  const handleSelection = (value: string) => {
    const newSelectedCampaigns = selectedCampaigns?.map(campaign => {
      if (campaign.campaignURN === value) {
        const modifiedCampaign = {
          ...campaign,
          isSelected: !campaign.isSelected,
        };
        return modifiedCampaign;
      }
      return campaign;
    });

    setSelectedCampaigns(newSelectedCampaigns);
  };

  const embeddedClass = isEmbeddedMode ? 'property-embedded' : '';
  const scrollingClass = hasNoScrolling ? 'footer-no-scrolling' : '';

  return (
    <>
      <div
        className={`subscribe__page client__specific d-flex flex-column ${embeddedClass}`}
      >
        <PageHeader branding={branding} />

        <CampaignList
          campaignList={selectedCampaigns!}
          handleSelection={handleSelection}
          hasNoScrolling={hasNoScrolling}
        />

        {isSignupComplete || isSuccessPageTest ? (
          <Row
            className={`subscribe__footer d-flex flex-column align-items-center m-0 ${scrollingClass}`}
          >
            <div className="subscribe__property__confirmation">
              <Tick />
              <span className="p-2">{branding.confirmationTitleCopy}</span>
            </div>
            <div className="subscribe__content__privacy mt-2">
              {branding.confirmationBodyCopy}
            </div>
          </Row>
        ) : (
          <Row
            className={`subscribe__footer align-items-center justify-content-center m-0 ${scrollingClass}`}
          >
            <div className="subscribe__variable__width">
              {branding?.isFirstNameLastNameEnabled && (
                <div className="subscribe__signup__content__container d-flex flex-column flex-md-row">
                  <Name
                    disabled={isSigningUp}
                    label={branding?.firstNameLabel ?? 'First name'}
                    onChange={handleFirstNameChange}
                    onKeyPress={handleKeypress}
                    value={firstName}
                  />
                  <Name
                    disabled={isSigningUp}
                    label={branding?.lastNameLabel ?? 'Last name'}
                    onChange={handleLastNameChange}
                    onKeyPress={handleKeypress}
                    value={lastName}
                  />
                </div>
              )}
              {branding?.customFieldsConfig &&
                branding?.customFieldDefinitions && (
                  <div className="subscribe__property__customfields">
                    {branding.customFieldsConfig.enabledFields
                      .sort((a, b) => a.order - b.order)
                      .map(field => {
                        const definition =
                          branding.customFieldDefinitions!.find(
                            def => def.dataName === field.dataName
                          );
                        if (!definition) return null;

                        return (
                          <CustomFieldInput
                            key={field.dataName}
                            field={field}
                            fieldDefinition={definition}
                            value={customFieldValues[definition.displayName]}
                            error={customFieldErrors[definition.displayName]}
                            onChange={value => {
                              setCustomFieldValues({
                                ...customFieldValues,
                                [definition.displayName]: value,
                              });
                              setCustomFieldErrors({
                                ...customFieldErrors,
                                [definition.displayName]: undefined,
                              });
                              setErrorMessage(null);
                            }}
                            disabled={isSigningUp}
                          />
                        );
                      })}
                  </div>
                )}
              <div className="subscribe__property__content__label">
                {branding.emailInputLabel} <span className="mandatory">*</span>
              </div>
              <div className="fit-content subscribe__email d-flex flex-column flex-md-row">
                <FormControl
                  className="subscribe__property__content__input"
                  type="email"
                  value={emailAddress}
                  disabled={isSigningUp}
                  onChange={handleChange}
                  onKeyPress={handleKeypress}
                />
                <SignupButton
                  disabled={disableButton}
                  isBusy={isSigningUp}
                  label={branding?.signupButtonLabel ?? 'Sign Up'}
                  onClick={handleSubmit}
                />
              </div>
              {errorMessage && (
                <div className="subscribe__content__error">{errorMessage}</div>
              )}
              <div className="subscribe__property__content__helper">
                {branding.helperText}
                {branding.propertyPrivacyPolicyEnabled && (
                  <>
                    <span> - </span>
                    <a href={`${branding.propertyPrivacyPolicyLink}`}>
                      {branding.propertyPrivacyPolicyText || 'Privacy Policy'}
                    </a>
                  </>
                )}
              </div>
            </div>
          </Row>
        )}
      </div>
    </>
  );
};

export default PropertySubscribe;
